import { useQuery, useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { type IRecipient, RecipientRole } from '../../../common/recipients/types';
import type { ISigner } from '../../../store/senderBuild/types';
import type { IResponse } from '../../types';
import { getRecipientColor } from '../../utils';
import type { QueryOptionsType } from './query-helpers';
import { getRecipients, updateRecipients, updateRecipient } from './recipients-api';

export function useGetRecipients(
  args: { envelopeId: string; signers: ISigner[] | undefined; allRecipients?: boolean },
  options?: QueryOptionsType<typeof getRecipients>
) {
  const { envelopeId, signers, allRecipients = false } = args;

  return useQuery(
    ['get-recipients', args],
    async () => {
      const recipients = await getRecipients(envelopeId, allRecipients);

      // Assign a color to each recipient
      for (const r of recipients) {
        if (r.title === 'Me') continue;
        const existingColor = signers?.find((s) => s.signerId === r.id)?.color;
        const newColor = getRecipientColor(r.recipientColorCounter);
        r.color = r.role === RecipientRole.CarbonCopy ? newColor : existingColor || newColor;
      }

      return recipients;
    },
    options
  );
}

export function useUpdateRecipients(
  options?: UseMutationOptions<IResponse<IRecipient[]>, unknown, { recipients: IRecipient[]; envelopeId: string }>
) {
  return useMutation(async ({ recipients, envelopeId }: { recipients: IRecipient[]; envelopeId: string }) => {
    return await updateRecipients(recipients, envelopeId);
  }, options);
}

export function useUpdateRecipient(
  options?: UseMutationOptions<IRecipient, unknown, { recipient: IRecipient; envelopeId: string }>
) {
  return useMutation(async ({ recipient, envelopeId }: { recipient: IRecipient; envelopeId: string }) => {
    return await updateRecipient(recipient, envelopeId);
  }, options);
}
